<template>
  <v-container class="ma-0 pa-0">
    {{ getTitle(cell) }}
    <v-row class="flex-nowrap bg-surface-variant" no-gutters>
      <v-col class="flex-grow-0 flex-shrink-0 align-self-center">
        <v-btn :class="{ 'mr-3': true, pulsing: isRecording }" fab small :color="isRecording ? 'red' : 'grey'" @click="toggleRecording">
          <v-icon color="white"> mdi-microphone </v-icon>
        </v-btn>
      </v-col>
      <v-col class="flex-grow-1 flex-shrink-0" cols="1" style="height: 54px; min-width: 100px; max-width: 100%">
        <canvas :class="{ hide: !isRecording }" ref="canvas" class="visualizer" style="height: 54px; width: 100%"></canvas>
        <audio :class="{ hide: isRecording || !recordingExists }" :src="audioSrc" ref="audio" controls>Your browser does not support the audio tag.</audio>
      </v-col>
      <v-col v-if="recordingExists && !isRecording" class="flex-grow-0 flex-shrink-0 align-self-center">
        <v-btn class="stop ml-3" fab small color="red" @click="deleteRecording">
          <v-icon color="white"> mdi-delete </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackBar" :timeout="5000" color="error" bottom>{{ error }}</v-snackbar>
  </v-container>
</template>

<script>
import { getTitle } from './wb-common';

export default {
  props: {
    cell: { type: Object, required: true },
    namespace: { type: String, required: true },
    mIndex: { type: String, default: '' },
  },
  data() {
    return {
      getTitle,
      recordingAvailable: false,
      isRecording: false,
      mediaRecorder: null,
      audioCtx: null,
      analyser: null,
      dataArray: null,
      bufferLength: null,
      canvasCtx: null,
      chunks: [],
      snackBar: false,
      error: '',
    };
  },
  computed: {
    input() {
      return this.$store.state[this.namespace].formInput;
    },
    cellKey() {
      return this.cell.key + this.mIndex;
    },
    audioSrc() {
      return this.recordingExists ? this.input[this.cellKey][0] : '';
    },
    recordingExists() {
      return this.input[this.cellKey] && Array.isArray(this.input[this.cellKey]) && this.input[this.cellKey].length > 0;
    },
  },
  methods: {
    toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    },
    startRecording() {
      this.isRecording = true;
      this.error = '';
      try {
        if (navigator.mediaDevices.getUserMedia) {
          let this_ = this;

          const constraints = { audio: true };

          let onSuccess = function (stream) {
            this_.mediaRecorder = new MediaRecorder(stream);
            this_.mediaRecorder.ondataavailable = (e) => {
              this_.chunks.push(e.data);
            };
            this_.mediaRecorder.onstop = (e) => {
              const blob = new Blob(this_.chunks, { type: this_.mediaRecorder.mimeType });
              this_.chunks = [];

              var reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = function () {
                var base64data = reader.result;
                this_.$set(this_.input, this_.cellKey, [base64data]);
              };
            };
            this_.mediaRecorder.start(1000);
            this_.visualizeAudio(stream);
          };

          let onError = function (err) {
            this_.isRecording = false;
            this_.error = this_.$t('module.unfalldesk.audio.error');
            this_.snackBar = true;
            console.debug(err);
          };

          navigator.mediaDevices.getUserMedia(constraints).then(onSuccess, onError);
        }
      } catch (err) {
        this.isRecording = false;
      }
    },
    stopRecording() {
      this.mediaRecorder.stop();
      this.isRecording = false;
    },
    deleteRecording() {
      this.$set(this.input, this.cellKey, []);
    },
    visualizeAudio(stream) {
      if (!this.audioCtx) {
        this.audioCtx = new AudioContext();
      }

      const source = this.audioCtx.createMediaStreamSource(stream);

      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(this.bufferLength);

      source.connect(this.analyser);

      this.drawAudio();
    },
    drawAudio() {
      try {
        const WIDTH = this.$refs['canvas'].clientWidth;
        const HEIGHT = this.$refs['canvas'].height;

        requestAnimationFrame(this.drawAudio);

        this.analyser.getByteTimeDomainData(this.dataArray);

        this.canvasCtx = this.$refs['canvas'].getContext('2d');

        this.canvasCtx.fillStyle = 'rgb(240, 240, 240)';
        this.canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

        this.canvasCtx.lineWidth = 3;
        this.canvasCtx.strokeStyle = 'rgb(120, 120, 120)';

        this.canvasCtx.beginPath();

        let sliceWidth = (WIDTH * 1.0) / this.bufferLength;
        let x = 0;

        for (let i = 0; i < this.bufferLength; i++) {
          let v = this.dataArray[i] / 128.0;
          let y = (v * HEIGHT) / 2;

          if (i === 0) {
            this.canvasCtx.moveTo(x, y);
          } else {
            this.canvasCtx.lineTo(x, y);
          }

          x += sliceWidth;
        }

        this.canvasCtx.lineTo(this.$refs['canvas'].clientWidth, this.$refs['canvas'].height / 2);
        this.canvasCtx.stroke();
      } catch (err) {
        console.warn(err);
      }
    },
  },
};
</script>

<style scoped>
audio {
  width: 100%;
}
canvas {
  border-radius: 27px;
}
.hide {
  display: none;
}
button.pulsing {
  box-shadow: 0 0 0 0 rgba(232, 76, 61, 0.7);
  animation: pulse 1.25s infinite cubic-bezier(0.66, 0, 0, 1);
}
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
  to {
    box-shadow: 0 0 0 15px rgba(232, 76, 61, 0);
  }
}
</style>
